import { ApiKeys } from '../../api/apis/ApiKeys';
import { PricebookApi } from '../../api/apis/PricebookApi';
import {
  ConfiguredDataSource,
  DataCompartmentOptions,
  IAppStorage,
  createDataCache,
  createDataCacheModule,
} from '@aesop-fables/scrinium';
import type { IServiceContainer } from '@aesop-fables/containr';
import {
  PricebookBundle,
  AvailablePricebooks,
  PricebookBundleWithUsage,
} from '../../models/PricebookData';
import { SelectionData } from '../../api/apis/PricebookApi';
import { OrganizationSelected } from '../../predicates/OrganizationSelected';
import { TeamRevenueAccessResolved } from '../../predicates/TeamRevenueAccessResolved';
import { GroupRevenueAccessResolved } from '../../predicates/GroupRevenueAccessResolved';
import { RevenueAccessResolved } from '../../predicates/RevenueAccessResolved';

export interface PricebookCompartments {
  pricebookOptions: DataCompartmentOptions<PricebookBundle[] | undefined>;
  pricebookSelectionIds: DataCompartmentOptions<SelectionData[] | undefined>;
  orgAvailablePricebooks: DataCompartmentOptions<AvailablePricebooks | undefined>;
  teamAvailablePricebooks: DataCompartmentOptions<AvailablePricebooks | undefined>;
  teamPricebooksInUse: DataCompartmentOptions<PricebookBundleWithUsage[] | undefined>;
  groupAvailablePricebooks: DataCompartmentOptions<AvailablePricebooks | undefined>;
  allPricebookBundles: DataCompartmentOptions<PricebookBundle[] | undefined>;
}

export const pricebookStorageKey = 'data/pricebook';

export const withPricebookData = createDataCacheModule(
  (appStorage: IAppStorage, container: IServiceContainer) => {
    const cache = createDataCache<PricebookCompartments>({
      pricebookOptions: {
        loadingOptions: {
          strategy: 'auto',
          predicate: container.resolve(OrganizationSelected),
        },
        source: new ConfiguredDataSource(async () => {
          const pricebookApi = container.get<PricebookApi>(ApiKeys.Pricebook);
          const { data } = await pricebookApi.getAvailableOrgOptions();
          return data;
        }),
        defaultValue: undefined,
      },
      pricebookSelectionIds: {
        loadingOptions: {
          strategy: 'auto',
          predicate: container.resolve(OrganizationSelected),
        },
        source: new ConfiguredDataSource(async () => {
          const pricebookApi = container.get<PricebookApi>(ApiKeys.Pricebook);
          const { data } = await pricebookApi.getOrgSelections();
          return data;
        }),
        defaultValue: undefined,
      },
      orgAvailablePricebooks: {
        loadingOptions: {
          strategy: 'lazy',
          predicate: container.resolve(OrganizationSelected),
        },
        source: new ConfiguredDataSource(async () => {
          const pricebookApi = container.get<PricebookApi>(ApiKeys.Pricebook);
          const { data } = await pricebookApi.getAvailableOrgOptionsV2();
          return data;
        }),
        defaultValue: undefined,
      },
      teamAvailablePricebooks: {
        loadingOptions: {
          strategy: 'lazy',
          predicate: container.resolve(TeamRevenueAccessResolved),
        },
        source: new ConfiguredDataSource(async () => {
          const pricebookApi = container.get<PricebookApi>(ApiKeys.Pricebook);
          const { data } = await pricebookApi.getAvailableTeamOptions();
          return data;
        }),
        defaultValue: undefined,
      },
      teamPricebooksInUse: {
        loadingOptions: {
          strategy: 'lazy',
          predicate: container.resolve(TeamRevenueAccessResolved),
        },
        source: new ConfiguredDataSource(async () => {
          const pricebookApi = container.get<PricebookApi>(ApiKeys.Pricebook);
          const { data } = await pricebookApi.getAvailableTeamOptionsWithUsage();
          return data;
        }),
        defaultValue: undefined,
      },
      groupAvailablePricebooks: {
        loadingOptions: {
          strategy: 'lazy',
          predicate: container.resolve(GroupRevenueAccessResolved),
        },
        source: new ConfiguredDataSource(async () => {
          const pricebookApi = container.get<PricebookApi>(ApiKeys.Pricebook);
          const { data } = await pricebookApi.getAvailableGroupOptions();
          return data;
        }),
        defaultValue: undefined,
      },
      allPricebookBundles: {
        loadingOptions: {
          strategy: 'lazy',
          predicate: container.resolve(RevenueAccessResolved),
        },
        source: new ConfiguredDataSource(async () => {
          const pricebookApi = container.get<PricebookApi>(ApiKeys.Pricebook);
          const { data } = await pricebookApi.getAllPricebookBundles();
          return data;
        }),
        defaultValue: undefined,
      },
    });

    appStorage.store(pricebookStorageKey, cache);
  },
);
